import { ColDef, RowClickedEvent, RowDoubleClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, onGridReady } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { usePUR1040_R01Store } from "./Store_PUR1040_R01";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";

import { defaultPUR1040_R01HeaderDto, PUR1040_R01HeaderDto } from "./Dto_PUR1040_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'PUR1040_R01-grid-column-state';
// 숫자 컬럼 목록 (헤더 DTO 키 보장 필요 없으므로 문자열 배열로 유지)
const numberFields: string[] = ['salAmt'];

interface Props {
    gridRef: React.RefObject<AgGridReact<PUR1040_R01HeaderDto> | null>;
	handleCreateClick?: () => void;
}

export const Table_PUR1040_R01 = (props: Props) => {

	const { gridRef, handleCreateClick } = props;

    const { PUR1040_R01List = [], 
        selectedPUR1040_R01, setSelectedPUR1040_R01, initSelectedPUR1040_R01,
		columnState, setColumnState, 
        PUR1040_R01DetailList = [], setPUR1040_R01DetailList,
		scrollTop, setScrollTop,
		setDetailScrollTop,
    } = usePUR1040_R01Store() as any;


	//const gridRef = useRef<AgGridReact>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		ensureSelectedRow,
    } = useGridSettingContent<PUR1040_R01HeaderDto>({
		gridRef,
		key: 'purchaseOrderNo',
        gridData: PUR1040_R01List,
        defaultGridData: defaultPUR1040_R01HeaderDto,
        selectedRow: selectedPUR1040_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		compNm: '사업장',
		orderDt: '발주일자',
		customCd: '거래처',
		customNm: '상호',
		vatTypeNm: '부가세',
		empNm: '발주담당',
		logicCenterNm: '입고물류',
		orderAmt: '매입가',
		vatAmt: '부가세',
		totalAmt: '매입총액',
		dueDt: '납기',
		approveFlg: '승인여부',
		orderStatusNm: '진행상태',
		telNo: '전화',
		faxNo: '팩스',
		email: 'e-mail',
	}

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compCd')(params), []);

    const baseColumnDefs: ColDef<PUR1040_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: headerNames.compNm, colId: 'compNm', field: 'compNm', width: 100,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: headerNames.orderDt, colId: 'orderDt', field: 'orderDt', width: 110,
			cellStyle: (p) => getCellStyle(p, 'orderDt'),
			headerClass: aggridHeaderClass,
			valueFormatter: (p: { data?: PUR1040_R01HeaderDto }) =>
				p.data?.orderDt ? formatDate(new Date(p.data.orderDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: headerNames.customCd, colId: 'customCd', field: 'customCd', width: 90,
			cellStyle: (p) => getCellStyle(p, 'customCd'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: headerNames.customNm, colId: 'customNm', field: 'customNm', width: 160,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: headerNames.vatTypeNm, colId: 'vatTypeNm', field: 'vatTypeNm', width: 80,
			cellStyle: (p) => getCellStyle(p, 'vatTypeNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: headerNames.empNm, colId: 'empNm', field: 'empNm', width: 90,
			cellStyle: (p) => getCellStyle(p, 'empNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: headerNames.logicCenterNm, colId: 'logicCenterNm', field: 'logicCenterNm', width: 100,
			cellStyle: (p) => getCellStyle(p, 'logicCenterNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: headerNames.orderAmt, colId: 'orderAmt', field: 'orderAmt', width: 110,
			cellStyle: (p) => getCellStyle(p, 'orderAmt'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: headerNames.vatAmt, colId: 'vatAmt', field: 'vatAmt', width: 90,
			cellStyle: (p) => getCellStyle(p, 'vatAmt'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: headerNames.totalAmt, colId: 'totalAmt', field: 'totalAmt', width: 110,
			cellStyle: (p) => getCellStyle(p, 'totalAmt'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: headerNames.dueDt, colId: 'dueDt', field: 'dueDt', width: 110,
			cellStyle: (p) => getCellStyle(p, 'dueDt'),
			headerClass: aggridHeaderClass,
			valueFormatter: (p: { data?: PUR1040_R01HeaderDto }) =>
				p.data?.dueDt ? formatDate(new Date(p.data.dueDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: headerNames.approveFlg, colId: 'approveFlg', field: 'approveFlg', width: 80,
			cellStyle: (p) => getCellStyle(p, 'approveFlg'),
			headerClass: aggridHeaderClass,
			valueFormatter: (p) => p.value === 'Y' ? 'Y' : 'N',
		},
		{
			headerName: headerNames.orderStatusNm, colId: 'orderStatusNm', field: 'orderStatusNm', width: 100,
			cellStyle: (p) => getCellStyle(p, 'orderStatusNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: headerNames.telNo, colId: 'telNo', field: 'telNo', width: 120,
			cellStyle: (p) => getCellStyle(p, 'telNo'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: headerNames.faxNo, colId: 'faxNo', field: 'faxNo', width: 120,
			cellStyle: (p) => getCellStyle(p, 'faxNo'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: headerNames.email, colId: 'email', field: 'email', width: 200,
			cellStyle: (p) => getCellStyle(p, 'email'),
			headerClass: aggridHeaderClass,
		},
    ], [getCellStyle, cellDateComparator, headerNames]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

    const columnDefs: ColDef<PUR1040_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
        return GridCustomColumnDefs<PUR1040_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);


	// 마운트 시 컬럼 상태 복원
	
    const handleRowClick = useCallback((e: RowClickedEvent<PUR1040_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 표와 정보가 같은지 확인 - purchaseOrderNo 사용
		const currentKey = rowData.purchaseOrderNo;
        const selectedKey = selectedPUR1040_R01?.purchaseOrderNo;
		const isSelected = selectedKey === currentKey;
		
		if (isSelected) {
            setSelectedPUR1040_R01(undefined);
			setDetailScrollTop(null);
			handleCreateClick && handleCreateClick();
		} else {
			setDetailScrollTop(null);
            setSelectedPUR1040_R01(rowData);
		}
    }, [selectedPUR1040_R01, setSelectedPUR1040_R01, setDetailScrollTop, handleCreateClick]);

	
    const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
        compCd: PUR1040_R01List.length.toLocaleString() + "건",
		approveFlg: '',
		orderAmt: PUR1040_R01List.reduce((sum: number, item: PUR1040_R01HeaderDto) => (sum + (item.orderAmt ?? 0)), 0),
		vatAmt: PUR1040_R01List.reduce((sum: number, item: PUR1040_R01HeaderDto) => (sum + (item.vatAmt ?? 0)), 0),
        totalAmt: PUR1040_R01List.reduce((sum: number, item: PUR1040_R01HeaderDto) => (sum + (item.totalAmt ?? 0)), 0).toLocaleString(),
		editable: false,
    }]), [PUR1040_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [PUR1040_R01List, selectedPUR1040_R01]);

	return (
		
        <GridContextWrapper<PUR1040_R01HeaderDto>
        	enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
            initSelected={initSelectedPUR1040_R01}
			defaultRowData={defaultPUR1040_R01HeaderDto}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
            <Grid_Touch<PUR1040_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				
                rowClassRules={createRowClassRules<PUR1040_R01HeaderDto>({
					idKey: 'purchaseOrderNo',
                    selectedList: selectedPUR1040_R01?.purchaseOrderNo ? [selectedPUR1040_R01.purchaseOrderNo] : [],
					deletedList: [],
					
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}	
				
				// Grid_Select 공통 로직 처리를 위한 props 전달
				setHoveredRowIndex={setHoveredRowIndex}		//마우스 오버 상태 이벤트를 위한 핸들러
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 (로컬스토리)	
				setColumnState={setColumnState}			//컬럼 상태 저장
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	)
}
